import Slide from "@mui/material/Slide";
import { createElement, forwardRef } from "react";
import { brand } from "brand/theme";

/** Enter from the right edge; exit back to the right (top-right toasts). */
const SlideFromRight = forwardRef(function SlideFromRight(props, ref) {
  return createElement(Slide, { ...props, direction: "left", ref });
});

const BAR = {
  success: brand.colors.accent,
  primary: brand.colors.accent,
  error: brand.colors.critical,
  warning: "#B45309",
  info: brand.colors.ink,
  secondary: brand.colors.muted,
};

const toastAlertSx = {
  minWidth: 300,
  maxWidth: 440,
  color: brand.colors.ink,
  backgroundColor: brand.colors.surface,
  border: `1px solid ${brand.colors.line}`,
  borderRadius: brand.radius,
  boxShadow: "0 16px 40px rgba(17, 24, 39, 0.12)",
  alignItems: "center",
  padding: "14px 16px 14px 18px",
  fontFamily: brand.fonts.body,
  overflow: "hidden",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    width: 4,
    backgroundColor: BAR.primary,
  },
  "& .MuiAlert-icon": {
    display: "none",
  },
  "& .MuiAlert-message": {
    padding: 0,
    paddingRight: 8,
    fontSize: "0.9375rem",
    fontWeight: 500,
    lineHeight: 1.45,
    color: brand.colors.ink,
    fontFamily: brand.fonts.body,
  },
  "& .MuiAlert-action": {
    marginRight: 0,
    paddingLeft: 4,
    paddingTop: 0,
    alignItems: "center",
    "& .MuiIconButton-root": {
      color: brand.colors.muted,
      padding: 4,
      borderRadius: 'var(--radius)',
      "&:hover": {
        backgroundColor: brand.colors.accentSoft,
        color: brand.colors.accent,
      },
    },
    "& .MuiButton-root": {
      fontFamily: brand.fonts.body,
      fontWeight: 600,
      borderRadius: 'var(--radius)',
      textTransform: "none",
    },
  },
  "&.MuiAlert-standardSuccess, &.MuiAlert-filledSuccess, &.MuiAlert-outlinedSuccess":
    {
      backgroundColor: brand.colors.surface,
      "&::before": { backgroundColor: BAR.success },
    },
  "&.MuiAlert-standardError, &.MuiAlert-filledError, &.MuiAlert-outlinedError": {
    backgroundColor: brand.colors.surface,
    "&::before": { backgroundColor: BAR.error },
  },
  "&.MuiAlert-standardWarning, &.MuiAlert-filledWarning, &.MuiAlert-outlinedWarning":
    {
      backgroundColor: brand.colors.surface,
      "&::before": { backgroundColor: BAR.warning },
    },
  "&.MuiAlert-standardInfo, &.MuiAlert-filledInfo, &.MuiAlert-outlinedInfo": {
    backgroundColor: brand.colors.surface,
    "&::before": { backgroundColor: BAR.info },
  },
  "&.MuiAlert-standardPrimary, &.MuiAlert-filledPrimary, &.MuiAlert-outlinedPrimary":
    {
      backgroundColor: brand.colors.surface,
      "&::before": { backgroundColor: BAR.primary },
    },
  "&.MuiAlert-standardSecondary, &.MuiAlert-filledSecondary, &.MuiAlert-outlinedSecondary":
    {
      backgroundColor: brand.colors.surface,
      "&::before": { backgroundColor: BAR.secondary },
    },
};

const toastDefaultSx = {
  "& .MuiSnackbarContent-root": {
    minWidth: 300,
    maxWidth: 440,
    color: brand.colors.ink,
    backgroundColor: brand.colors.surface,
    border: `1px solid ${brand.colors.line}`,
    borderRadius: brand.radius,
    boxShadow: "0 16px 40px rgba(17, 24, 39, 0.12)",
    padding: "14px 16px 14px 18px",
    fontFamily: brand.fonts.body,
    fontSize: "0.9375rem",
    fontWeight: 500,
    overflow: "hidden",
    position: "relative",
    "&::before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      width: 4,
      backgroundColor: brand.colors.accent,
    },
  },
  "& .MuiSnackbarContent-message": {
    padding: 0,
    color: brand.colors.ink,
    fontFamily: brand.fonts.body,
  },
  "& .MuiSnackbarContent-action": {
    marginRight: 0,
    paddingLeft: 8,
    "& .MuiIconButton-root": {
      color: brand.colors.muted,
      borderRadius: 'var(--radius)',
      "&:hover": {
        backgroundColor: brand.colors.accentSoft,
        color: brand.colors.accent,
      },
    },
  },
};

// ==============================|| OVERRIDES - SNACKBAR ||============================== //

const HEADER_OFFSET_XS = 64 + 12;
const HEADER_OFFSET_SM = 68 + 12;

export default function Snackbar() {
  return {
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: "top", horizontal: "right" },
        TransitionComponent: SlideFromRight,
        autoHideDuration: 4000,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          "&.MuiSnackbar-anchorOriginTopRight, &.MuiSnackbar-anchorOriginTopLeft, &.MuiSnackbar-anchorOriginTopCenter":
            {
              top: HEADER_OFFSET_XS,
              [theme.breakpoints.up("sm")]: {
                top: HEADER_OFFSET_SM,
              },
            },
          "& .MuiAlert-root": toastAlertSx,
          ...toastDefaultSx,
        }),
      },
    },
  };
}
